import React, { useEffect, useState } from "react";
import { adminEnquiries, updateEnquiryStatus, API } from "@/lib/api";
import { toast } from "sonner";
import { Paperclip, DownloadSimple } from "@phosphor-icons/react";

const STATUSES = ["New", "Under Review", "Contacted", "Scheduled", "Completed", "Closed"];
const TYPE_LABELS = {
  consultation: "Consultation", second_opinion: "Second Opinion", research: "Research", media: "Media",
  speaking: "Speaking", ngo: "NGO / Impact", mentorship: "Mentorship", general: "General", volunteer: "Volunteer",
  partnership: "Partnership", collaborate: "Collaboration", donation: "Donation / Support",
};
const STATUS_COLORS = { New: "bg-gold/20 text-gold-dark", "Under Review": "bg-blue-100 text-blue-700", Contacted: "bg-purple-100 text-purple-700", Scheduled: "bg-amber-100 text-amber-700", Completed: "bg-green-100 text-green-700", Closed: "bg-slate-200 text-slate-600" };

export default function EnquiriesManager() {
  const [items, setItems] = useState([]);
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [open, setOpen] = useState(null);
  const token = localStorage.getItem("bs_token");

  const load = () => adminEnquiries({ ...(type && { type }), ...(status && { status }) }).then(setItems).catch(() => setItems([]));
  useEffect(() => { load(); }, [type, status]);

  const changeStatus = async (id, s) => { await updateEnquiryStatus(id, s); toast.success("Status updated"); load(); if (open?.id === id) setOpen({ ...open, status: s }); };

  return (
    <div data-testid="manager-enquiries">
      <h2 className="font-serif text-3xl text-navy">Enquiries</h2>
      <div className="mt-4 mb-6 flex flex-wrap gap-3">
        <select value={type} onChange={(e) => setType(e.target.value)} data-testid="enq-filter-type" className="rounded-md border border-slate-300 px-3 py-2 text-sm">
          <option value="">All types</option>{Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="enq-filter-status" className="rounded-md border border-slate-300 px-3 py-2 text-sm">
          <option value="">All statuses</option>{STATUSES.map((s) => <option key={s}>{s}</option>)}
        </select>
      </div>
      <div className="overflow-hidden rounded-lg border border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-softgrey-100 text-left text-xs uppercase tracking-wide text-softgrey-600">
            <tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Received</th><th /></tr>
          </thead>
          <tbody>
            {items.map((e) => (
              <tr key={e.id} className="border-t border-slate-100 hover:bg-softgrey-100">
                <td className="px-4 py-3"><div className="font-medium text-navy">{e.name || "—"}</div><div className="text-xs text-softgrey-400">{e.email}</div></td>
                <td className="px-4 py-3">{TYPE_LABELS[e.type] || e.type}</td>
                <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_COLORS[e.status] || ""}`}>{e.status}</span></td>
                <td className="px-4 py-3 text-xs text-softgrey-400">{new Date(e.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-3 text-right"><button data-testid={`view-enq-${e.id}`} onClick={() => setOpen(e)} className="text-sm font-semibold text-gold-dark">View</button></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-softgrey-600">No enquiries.</td></tr>}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 py-10" onClick={() => setOpen(null)}>
          <div className="w-full max-w-xl rounded-lg bg-white p-8" onClick={(ev) => ev.stopPropagation()} data-testid="enquiry-detail">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-serif text-2xl text-navy">{TYPE_LABELS[open.type]} — {open.name}</h3>
              <span className="text-xs text-softgrey-400">Ref {open.id.slice(0, 8)}</span>
            </div>
            <div className="space-y-2 text-sm">
              {Object.entries(open).filter(([k, v]) => !["id", "type", "status", "created_at", "attachments", "_id"].includes(k) && v).map(([k, v]) => (
                <div key={k} className="flex gap-3 border-b border-slate-100 py-2"><span className="w-40 shrink-0 font-medium capitalize text-navy/70">{k.replace(/_/g, " ")}</span><span className="text-navy">{String(v)}</span></div>
              ))}
            </div>
            {open.attachments?.length > 0 && (
              <div className="mt-4">
                <div className="text-xs font-semibold uppercase text-softgrey-600">Documents</div>
                <ul className="mt-2 space-y-1">
                  {open.attachments.map((a) => (
                    <li key={a.id}><a href={`${API}/files/${a.id}?auth=${token}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-gold-dark"><Paperclip size={14} /> {a.filename} <DownloadSimple size={14} /></a></li>
                  ))}
                </ul>
              </div>
            )}
            <div className="mt-6">
              <label className="mb-1.5 block text-[13px] font-medium text-navy/80">Update Status</label>
              <select data-testid="enq-status-select" value={open.status} onChange={(e) => changeStatus(open.id, e.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm">
                {STATUSES.map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
            <button onClick={() => setOpen(null)} className="mt-6 rounded-md border border-slate-300 px-6 py-2.5 text-sm font-semibold text-navy">Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
